import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CancellationModal = ({ isOpen, onClose, onSubmit, assetName }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const cancellationReasons = [
    "Changed my mind about the investment",
    "Found a better investment opportunity",
    "Financial constraints",
    "Don't need the asset anymore",
    "Unhappy with the terms and conditions",
    "Other personal reasons"
  ];

  const handleSubmit = async () => {
    if (!selectedReason) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(selectedReason);
      onClose();
    } catch (error) {
      console.error('Cancellation request failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Request Cancellation</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            You are requesting to cancel your booking for <span className="font-semibold">{assetName}</span>.
          </p>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Please select a reason for cancellation:
            </label>
            <div className="space-y-2">
              {cancellationReasons.map((reason, index) => (
                <label
                  key={index}
                  className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="radio"
                    name="cancellation-reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">{reason}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">
              <strong>Note:</strong> Once you submit this request, your asset will be locked for review. 
              The admin will review your request and respond accordingly.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            onClick={onClose}
            variant="outline"
            disabled={submitting}
            className="min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            style={{ backgroundColor: '#DC2626', color: '#FFF' }}
            className="min-h-[44px] hover:opacity-90"
          >
            {submitting ? 'Submitting...' : 'Submit Cancellation Request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CancellationModal;
